// Single source of truth for every value the planner wizard can send to
// get_top_destination_matches_v3. If you add a new accepted value here,
// you MUST also add it to the corresponding CASE/IN list in the SQL
// function body, and refresh the fixture at
// src/lib/__tests__/fixtures/v3-match-lists.snapshot.ts (see that file's
// header comment for how). The contract test in
// src/lib/__tests__/planner-vocabulary-contract.test.ts will fail if you
// forget either step.

export const SKILL_LEVEL_VALUES = ['beginner', 'intermediate', 'advanced', 'expert'] as const;
export type SkillLevel = (typeof SKILL_LEVEL_VALUES)[number];

// What the surfer wants to achieve in the water. Deliberately excludes trip
// atmosphere (social, relaxation, adventure, nightlife) -- see TRIP_VIBE_VALUES
// below for that, a separate signal as of the surf-objective/trip-vibe split.
export const SURF_GOAL_VALUES = ['learn', 'progress', 'score', 'consistent'] as const;
export type SurfGoal = (typeof SURF_GOAL_VALUES)[number];

// What atmosphere the surfer wants around the surfing -- independent of surf
// objective. 'explore' | 'relax' | 'social' remain accepted by the SQL
// function as legacy p_trip_goal values (they fall through to its generic
// fallback formula) purely so a stale cached client mid-rollout degrades
// gracefully; do not add them back here or route new UI to them.
export const TRIP_VIBE_VALUES = ['remote', 'relaxed', 'social', 'party'] as const;
export type TripVibe = (typeof TRIP_VIBE_VALUES)[number];

export const BUDGET_VALUES = ['$', '$$', '$$$'] as const;
export type Budget = (typeof BUDGET_VALUES)[number];

export const WATER_TEMP_VALUES = ['warm', 'moderate', 'cold', 'any'] as const;
export type WaterTemp = (typeof WATER_TEMP_VALUES)[number];

// RETIRED from the wizard UI as of the surf-objective/trip-vibe split:
// 'comfortable' and 'adventure' now overlap with tripVibe's 'relaxed' and
// 'remote', and 'balanced' was a neutral default rather than a distinct
// preference -- asking both questions meant measuring the same thing twice
// and double-weighting adventure/ease in scoring. No wizard code sends a
// value for this any more (see TripPlannerWizard.tsx). Kept here, and kept
// live in the SQL function, only because get_top_destination_matches_v3 is
// reachable directly from the browser and must stay defensive/functional
// for any well-formed input, not just the current wizard's -- per CLAUDE.md
// §16. Do not wire this back into new UI; extend tripVibe instead.
export const EXPERIENCE_STYLE_VALUES = ['comfortable', 'balanced', 'adventure'] as const;
export type ExperienceStyle = (typeof EXPERIENCE_STYLE_VALUES)[number];

export const CROWD_TOLERANCE_VALUES = ['none', 'some', 'lot'] as const;
export type CrowdTolerance = (typeof CROWD_TOLERANCE_VALUES)[number];
